import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MovieCard from "@/components/base/MovieCard";
import FilterRow from "./FilterRow";
import {
  movies,
  tvShows,
  movieGenres,
  tvGenres,
  regions,
  years,
  episodeRanges,
} from "@/mocks/media";

type MediaType = "movie" | "tv";
type SortKey = "最新" | "最热" | "评分";

interface CategoryViewProps {
  type: MediaType;
}

const PAGE_SIZE = 8;

const sortOptions: SortKey[] = ["最新", "最热", "评分"];

export default function CategoryView({ type }: CategoryViewProps) {
  const isMovie = type === "movie";
  const source = isMovie ? movies : tvShows;
  const genres = isMovie ? movieGenres : tvGenres;

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [episode, setEpisode] = useState("全部");
  const [sort, setSort] = useState<SortKey>("最新");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = source.filter((item) => {
      if (genre !== "全部" && !item.genres.includes(genre)) return false;
      if (region !== "全部" && item.region !== region) return false;
      if (year !== "全部" && String(item.year) !== year) return false;
      if (!isMovie && episode !== "全部") {
        if (episode === "20 集以内" && item.episodes > 20) return false;
        if (episode === "21-40 集" && (item.episodes < 21 || item.episodes > 40)) return false;
        if (episode === "40 集以上" && item.episodes <= 40) return false;
      }
      return true;
    });

    if (sort === "最新") list = [...list].sort((a, b) => b.year - a.year);
    if (sort === "最热") list = [...list].sort((a, b) => b.views - a.views);
    if (sort === "评分") list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [source, genre, region, year, episode, sort, isMovie]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice(0, page * PAGE_SIZE);

  const updateFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setEpisode("全部");
    setSort("最新");
    setPage(1);
  };

  const title = isMovie ? "电影分类" : "电视剧分类";
  const intro = isMovie
    ? "汇集动作、科幻、悬疑、爱情等各类高清电影资源，按类型、地区、年份自由筛选，每日持续更新佳片。"
    : "覆盖古装、都市、悬疑、科幻等多题材电视剧，支持按集数区间筛选，连载剧集一站追更。";

  return (
    <div className="w-full">
      <div className="bg-background-100 border-b border-background-300">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
            <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
              主页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{isMovie ? "电影" : "电视剧"}</span>
          </nav>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading flex items-center gap-3">
            <span className={`w-9 h-9 flex items-center justify-center rounded-md ${isMovie ? "bg-primary-500" : "bg-accent-500"}`}>
              <i className={`${isMovie ? "ri-movie-2-line" : "ri-live-line"} text-xl text-background-50`}></i>
            </span>
            {title}
          </h1>
          <p className="text-sm text-foreground-600 mt-3 max-w-2xl leading-relaxed">{intro}</p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8">
        <div className="rounded-lg border border-background-300 bg-background-100 p-4 md:p-5 mb-8">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="md:hidden w-full flex items-center justify-between text-sm font-medium text-foreground-900 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-primary-400"></i>
              筛选条件
            </span>
            <i className={filtersOpen ? "ri-arrow-up-s-line text-lg" : "ri-arrow-down-s-line text-lg"}></i>
          </button>

          <div className={`${filtersOpen ? "block" : "hidden"} md:block mt-3 md:mt-0`}>
            <FilterRow label="类型" options={genres} value={genre} onChange={updateFilter(setGenre)} />
            <FilterRow label="地区" options={regions} value={region} onChange={updateFilter(setRegion)} />
            <FilterRow
              label="年份"
              options={years.map(String)}
              value={year}
              onChange={updateFilter(setYear)}
            />
            {!isMovie && (
              <FilterRow
                label="集数"
                options={episodeRanges.filter((r) => r !== "全部")}
                value={episode}
                onChange={updateFilter(setEpisode)}
              />
            )}
            <FilterRow
              label="排序"
              options={sortOptions}
              value={sort}
              onChange={updateFilter((v) => setSort(v as SortKey))}
            />
          </div>
        </div>

        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-foreground-500">
            共找到 <span className="text-primary-400 font-medium">{filtered.length}</span> 部{isMovie ? "电影" : "电视剧"}
          </p>
          {(genre !== "全部" || region !== "全部" || year !== "全部" || episode !== "全部" || sort !== "最新") && (
            <button
              type="button"
              onClick={resetFilters}
              className="text-sm text-foreground-500 hover:text-accent-400 transition-colors cursor-pointer flex items-center gap-1"
            >
              <i className="ri-close-circle-line"></i>
              清空筛选
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 rounded-lg border border-dashed border-background-300">
            <span className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 mb-4">
              <i className="ri-inbox-2-line text-3xl text-foreground-500"></i>
            </span>
            <p className="text-foreground-800 mb-1">暂无相关影片</p>
            <p className="text-sm text-foreground-500 mb-5">试试调整或清空筛选条件</p>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              清空筛选
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {visible.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>

            {page < totalPages && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md border border-background-300 bg-background-100 text-foreground-800 text-sm font-medium hover:border-primary-400 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  加载更多
                </button>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 cursor-pointer"
                  aria-label="上一页"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={`w-9 h-9 flex items-center justify-center rounded-md text-sm cursor-pointer transition-colors ${
                      n === page
                        ? "bg-primary-500 text-background-50 font-medium"
                        : "border border-background-300 text-foreground-700 hover:border-primary-400"
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 cursor-pointer"
                  aria-label="下一页"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}